/* eslint no-nested-ternary: "off" */
/* eslint no-param-reassign: "off" */

import axios from 'axios';

import { AxiosError } from 'api/axios';
import { API_TIMEOUTS, apiBaseURL } from 'api/settings';
import * as errors from 'common/errors';
import * as utility from 'common/utility';
import { mandatory } from 'common/validation';

interface PostSubscribeProps {
  campaignKey: string;
  emailAddress: string;
  firstName?: string;
  lastName?: string;
  customFields?: Record<string, any>;
}

interface PostSubscribeParams {
  campaignURN: string;
  email: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any; // Allow custom fields as top-level properties
}

export default async function postSubscribe({
  campaignKey,
  emailAddress,
  firstName,
  lastName,
  customFields,
}: PostSubscribeProps) {
  if (utility.isUndefined(campaignKey)) {
    mandatory('campaignKey');
  }
  if (utility.isUndefined(emailAddress)) {
    mandatory('emailAddress');
  }

  // Handle comma-separated campaign URNs for multi-campaign subscriptions
  // Backend splits by comma, so we send the string as-is
  let campaignURN = campaignKey.trim();

  // Only add prefix if it's a single campaign without URN format
  // For comma-separated URNs, we send them as-is since backend splits them
  if (!campaignURN.includes(',') && campaignURN.split(':').length === 1) {
    campaignURN = `urn:newsletter:campaign:${campaignURN}`;
  }

  const params: PostSubscribeParams = {
    campaignURN,
    email: emailAddress,
  };
  if (firstName !== '') {
    params.firstName = firstName;
  }
  if (lastName !== '') {
    params.lastName = lastName;
  }

  // Add custom fields as top-level properties
  // Backend uses @JsonAnySetter to accept arbitrary fields
  if (customFields) {
    Object.entries(customFields).forEach(([key, value]) => {
      // Only add non-empty values
      if (value !== undefined && value !== null && value !== '') {
        // Convert arrays to comma-separated strings (for SET_STRING fields)
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params[key] = value.join(', ');
          }
        } else {
          params[key] = value;
        }
      }
    });
  }
  const config = {
    url: `${apiBaseURL()}/public/campaigns/subscribe`,
    method: 'POST',
    timeout: API_TIMEOUTS.S,
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(params),
  };

  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    // Backend validates custom fields and returns 400 Bad Request on validation errors
    // Custom field errors are caught here and propagated to the caller
    const apiError =
      error instanceof AxiosError
        ? error.message
        : error instanceof Error
          ? // @ts-expect-error need to sort this out one day
            await errors.handleAPIError({ originalError: error })
          : error;
    throw apiError;
  }
}
